import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { fetchUserInfo, setCurrentStore as apiSetCurrentStore } from '@/api/common'
import { isWorkWechat } from '@/utils/mobile'
import { workWxCodeLogin } from '@/hooks/useWorkWxLogin'
import type { UserInfo } from '@/api/types/common'

interface AuthContextType {
  userInfo: UserInfo | null
  isAuthenticated: boolean
  loading: boolean
  refreshUserInfo: () => Promise<void>
  setUserInfo: (userInfo: UserInfo | null) => void
  // 角色切换相关（仅 admin 可用）
  effectiveRoleCode: string // 当前生效的角色代码（考虑覆盖）
  setRoleOverride: (roleCode: string) => void // 设置临时角色覆盖
  clearRoleOverride: () => void // 清除角色覆盖
  // 当前门店
  currentStoreId: string // 当前选中门店ID
  currentStoreName: string // 当前选中门店名称
  switchStore: (storeId: string) => Promise<void> // 切换门店
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// 角色覆盖存储在 sessionStorage 的 key
const ROLE_OVERRIDE_KEY = '__role_override__'

// 不需要登录的页面路径
const PUBLIC_PATHS = new Set([
  '/workbench',
  '/vehicle',
  '/mine',
  '/login',
  '/user-form', // 客户邀请填写身份信息页面
  '/todo-user-form', // 待办邀请用户填写信息页面
  '/todo-user-detail', // 待办用户独立查看页面
  '/pickup-order-share', // 提货单据分享详情页面
])

// 检查当前路径是否为公开页面
export const isPublicPath = (pathname: string): boolean => {
  // 精确匹配
  if (PUBLIC_PATHS.has(pathname)) {
    return true
  }
  // 前缀匹配（如 /user-form/xxx）
  for (const path of PUBLIC_PATHS) {
    if (pathname.startsWith(path + '/')) {
      return true
    }
  }
  return false
}

/**
 * 获取存储的角色覆盖
 */
function getStoredRoleOverride(): string | null {
  try {
    return sessionStorage.getItem(ROLE_OVERRIDE_KEY)
  } catch {
    return null
  }
}

/**
 * 设置存储的角色覆盖
 */
function setStoredRoleOverride(roleCode: string | null): void {
  try {
    if (roleCode) {
      sessionStorage.setItem(ROLE_OVERRIDE_KEY, roleCode)
    } else {
      sessionStorage.removeItem(ROLE_OVERRIDE_KEY)
    }
  } catch (error) {
    console.warn('Failed to store role override:', error)
  }
}

/**
 * 认证 Provider
 * 在应用启动时统一获取用户信息，避免各页面重复调用
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const isLoadingRef = useRef(false)
  const mountedRef = useRef(true)
  const [roleOverride, setRoleOverrideState] = useState<string | null>(getStoredRoleOverride())

  // 获取用户信息（不自动管理 loading，由调用方控制）
  const fetchUserInfoOnly = useCallback(async (): Promise<UserInfo | null> => {
    if (isLoadingRef.current) {
      return null
    }
    isLoadingRef.current = true

    try {
      const data = await fetchUserInfo()
      if (mountedRef.current) {
        setUserInfo(data)
      }
      return data
    } catch (error) {
      if (mountedRef.current) {
        setUserInfo(null)
      }
      return null
    } finally {
      isLoadingRef.current = false
    }
  }, [])

  // 应用启动时获取用户信息
  useEffect(() => {
    mountedRef.current = true

    // 延迟初始化，避免阻塞渲染
    const timer = setTimeout(async () => {
      if (!mountedRef.current) return

      // 先获取用户信息，判断是否已登录
      const currentUserInfo = await fetchUserInfoOnly()
      if (!mountedRef.current) return

      // 未登录 + 企业微信环境 → 尝试授权码登录
      if (!currentUserInfo && isWorkWechat()) {
        const loginSuccess = await workWxCodeLogin()
        if (!mountedRef.current) return
        if (loginSuccess) {
          await fetchUserInfoOnly()
        }
      }

      // 未登录也设置 loading 为 false，避免 RouteGuard 陷入无限循环
      // 登录状态已在 fetchUserInfoOnly 中正确设置
      if (mountedRef.current) {
        setLoading(false)
      }
    }, 100)

    return () => {
      mountedRef.current = false
      clearTimeout(timer)
    }
  }, [fetchUserInfoOnly])

  // 计算生效的角色代码（优先使用角色覆盖）
  const effectiveRoleCode = roleOverride || userInfo?.roleCode || 'employee'

  // 当前门店信息
  const currentStoreId = userInfo?.currentStoreId || ''
  const currentStoreName = userInfo?.currentStoreName || ''

  // 刷新用户信息（供外部调用）
  const refreshUserInfo = useCallback(async () => {
    await fetchUserInfoOnly()
    setLoading(false)
  }, [fetchUserInfoOnly])

  // 切换门店
  const switchStore = useCallback(async (storeId: string) => {
    await apiSetCurrentStore({ storeId })
    await fetchUserInfoOnly()
  }, [fetchUserInfoOnly])

  // 设置角色覆盖
  const setRoleOverride = useCallback((roleCode: string) => {
    setRoleOverrideState(roleCode)
    setStoredRoleOverride(roleCode)
  }, [])

  // 清除角色覆盖
  const clearRoleOverride = useCallback(() => {
    setRoleOverrideState(null)
    setStoredRoleOverride(null)
  }, [])

  const contextValue: AuthContextType = {
    userInfo,
    isAuthenticated: !!userInfo,
    loading,
    refreshUserInfo,
    setUserInfo,
    effectiveRoleCode,
    setRoleOverride,
    clearRoleOverride,
    currentStoreId,
    currentStoreName,
    switchStore,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * 使用认证状态 Hook
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * 检查是否可以切换角色（只有真正的 admin 可以）
 * @param userInfo 用户信息
 * @returns 是否可以切换角色
 */
export function canSwitchRole(userInfo: UserInfo | null): boolean {
  if (!userInfo) return false
  // 只要实际角色是 admin 就可以切换角色（包括将角色切回admin）
  return userInfo.roleCode === 'admin'
}

/**
 * 获取所有可切换的角色选项
 */
export function getRoleSwitchOptions(): Array<{ value: string; label: string; description: string }> {
  return [
    { value: 'admin', label: '管理员', description: '拥有所有权限' },
    { value: 'materialAdmin', label: '物料管理员', description: '可管理车辆和配件' },
    { value: 'employee', label: '员工', description: '只能查看数据' },
  ]
}
